import React, { useCallback, useRef, useState } from 'react';
import { Element, Range } from 'slate';
import { ReactEditor, useSlateStatic, useSelected } from 'slate-react';
import { Container, PlusButton } from './BlockMenu.styled';
import { Plus } from './Plus.svg';
import { fileBlockType } from '../../plugins/file/fileBlockType';
import { blockLinkType } from '../../plugins/blockLink/blockLinkType';
import { FileUploadInput } from '../FileUploadInput/FileUploadInput';
import { AulaBlockMenuProps } from '../../plugins/plugins';
import { unsupportedType } from '../../plugins/unsupported/UnsupportedElement';
import { useOutside } from '../../lib/useOutside';
import { blockQuoteType } from '../../plugins/blockQuote/blockQuoteType';
import { NestedMenu } from '../NestedMenu/NestedMenu';
import { latexType } from '../../plugins/latex/latexType';
import { codeType } from '../../plugins/code/codeType';
import { panoptoType } from '../../plugins/panopto/panoptoType';
import { planetEStreamType } from '../../plugins/planetEStream/planetEStreamType';
import { talisType } from '../../plugins/talis/talisType';
import { echo360Type } from '../../plugins/echo360/echo360Type';
import { bigBlueButtonType } from '../../plugins/bigBlueButton/bigBlueButtonType';
import { bibliUType } from '../../plugins/bibliU/bibliUType';
import { assignmentType } from '../../plugins/assignment/assignmentType';
import {
  orderedListType,
  unorderedListType,
} from '../../plugins/list/listType';
import { AulaEditorContext } from '../../types';

export type BlockMenuProps = {
  element: Element;
  context: Omit<AulaEditorContext, 'entities'>;
} & AulaBlockMenuProps;

export const topMargins: { [key: string]: number } = {
  h1: 23,
  h2: 22,
  h3: 18,
  [blockLinkType]: 13,
  [fileBlockType]: 13,
  [unsupportedType]: 13,
  [latexType]: 13,
  [codeType]: 13,
  [blockQuoteType]: 5,
  [panoptoType]: 13,
  [planetEStreamType]: 13,
  [talisType]: 13,
  [echo360Type]: 13,
  [bibliUType]: 13,
  [bigBlueButtonType]: 13,
  [assignmentType]: 13,
  [orderedListType]: 11,
  [unorderedListType]: 11,
  default: 10,
};

export const BlockMenu = ({
  element,
  blockMenu,
  context,
  renderSubMenuItem,
}: BlockMenuProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const editor = useSlateStatic();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selected =
    useSelected() &&
    editor.selection != null &&
    ReactEditor.hasTarget(editor, document.activeElement) &&
    Range.isCollapsed(editor.selection);

  const [showDropdown, setShowDropdown] = useState(false);

  const menuRef = useOutside<HTMLDivElement & { target: { closest: any } }>(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      /**
       * Because nested menus are rendered via Portals `useOutside`
       * does not work on nested menu items
       * therefore we exclude those via dom apis
       */
      if (target.closest('[role="menuitem"]') == null) {
        setShowDropdown(false);
      }
    },
  );

  const openFileDialog = useCallback(() => {
    const { current } = inputRef;
    if (current == null) {
      throw new Error('inputRef must exist');
    }
    setShowDropdown(false);
    current.click();
  }, [inputRef]);

  const onToggleDropdown = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setShowDropdown(!showDropdown);
      setAnchorEl(event.currentTarget);
    },
    [showDropdown],
  );

  const topMargin = topMargins[(element.type as string) || 'default'];

  return (
    <Container top={topMargin} contentEditable={false} data-testid="block-menu">
      <PlusButton
        data-block-button
        data-testid="block-menu-toggle"
        isSelected={showDropdown}
        tabIndex={selected ? 0 : -1}
        visible={selected}
        aria-hidden={selected ? 'false' : 'true'}
        aria-label="Open menu"
        onMouseDown={(e) => {
          e.preventDefault();
          ReactEditor.blur(editor);
        }}
        onClick={onToggleDropdown}
        ref={btnRef}
      >
        <Plus />
      </PlusButton>
      {showDropdown && anchorEl != null && (
        <div style={{ position: 'relative' }} tabIndex={-1} ref={menuRef}>
          <NestedMenu
            onClose={() => {
              setShowDropdown(false);
              setTimeout(() => {
                ReactEditor.focus(editor);
              }, 0);
            }}
            open={showDropdown}
            renderSubMenuItem={renderSubMenuItem}
            anchorEl={anchorEl}
            openFileDialog={openFileDialog}
            menuItems={blockMenu}
            element={element}
          />
        </div>
      )}
      <FileUploadInput
        context={context}
        element={element}
        ref={inputRef}
        onChange={() => {
          setShowDropdown(false);
          setTimeout(() => {
            ReactEditor.focus(editor);
          }, 100);
        }}
      />
    </Container>
  );
};
